import { TypedDocumentNode, gql } from '@apollo/client';
import { JSX, ReactElement } from 'react';
import styled from 'styled-components';

import { Rarity, ShippingState, Sport } from '__generated__/globalTypes';
import discountCardBack from 'assets/rewards/discount-card-back.png';
import { Title3 } from 'atoms/typography';
import { CardBack } from 'components/card/Back';
import { FRONTEND_ASSET_HOST } from 'constants/assets';
import { isType } from 'gql';
import { monetaryAmountFragment } from 'lib/monetaryAmount';
import { getPositionInitials } from 'lib/positions';

import { useReferralRewards_referralReward } from './__generated__/useReferralRewards.graphql';
import { useClaimReferralReward } from './useClaimReferralReward';

const DiscountCardBack = styled.img`
  width: var(--card-width);
  max-width: 100%;
`;

const isConversionCreditReward = (
  reward: useReferralRewards_referralReward
) => {
  return !!reward.conversionCredit;
};

export type Cardback = ReactElement | null;

const cardBackFactory = (
  reward: useReferralRewards_referralReward
): Cardback => {
  if (isConversionCreditReward(reward)) {
    return <DiscountCardBack src={discountCardBack} alt="" width={20} />;
  }
  if (isType(reward.card, 'Card')) {
    const { card } = reward;
    const isCustomSeriesReward = card.rarityTyped === Rarity.custom_series;

    return (
      <CardBack
        path={
          card.backPictureUrl ||
          `${FRONTEND_ASSET_HOST}/cards/back/${card.rarityTyped}.svg`
        }
        sport={Sport.FOOTBALL}
        rarity={card.rarityTyped}
        radius={isCustomSeriesReward ? '30px' : '10px'}
      />
    );
  }
  if (isType(reward.card, 'BaseballCard') || isType(reward.card, 'NBACard')) {
    return (
      <CardBack
        sport={isType(reward.card, 'BaseballCard') ? Sport.BASEBALL : Sport.NBA}
        rarity={reward?.card.rarityTyped}
      />
    );
  }
  return null;
};

export type Teasers = JSX.Element[];

const teaserFactory = (reward: useReferralRewards_referralReward): Teasers => {
  if (isConversionCreditReward(reward)) {
    return [];
  }
  const { card } = reward;
  if (isType(card, 'Card')) {
    return [
      <img key={0} src={card.player.activeClub?.pictureUrl || ''} alt="" />,
      <Title3 key={1}>{card.player.age}</Title3>,
      <Title3 key={2}>{card.player.cardPositions[0].slice(0, 3)}</Title3>,
    ];
  }

  if (!card) return [];

  const initials = getPositionInitials(card.player.anyPositions[0]);

  if (isType(card, 'BaseballCard') || isType(card, 'NBACard')) {
    return [
      <img key={0} src={card.anyTeam?.pictureUrl || ''} alt="" />,
      <Title3 key={1}>{initials}</Title3>,
    ];
  }
  return [];
};

export const useReferralRewards = () => {
  const claim = useClaimReferralReward();

  const isClaimed = (reward: useReferralRewards_referralReward | null) => {
    if (!reward) return false;
    return reward.shippingState === ShippingState.CLAIMED;
  };

  const makeCardBackAndTeaser = (reward: useReferralRewards_referralReward) => {
    return {
      cardback: cardBackFactory(reward),
      teasers: teaserFactory(reward),
    };
  };

  return {
    claim,
    isClaimed,
    makeCardBackAndTeaser,
  };
};

useReferralRewards.fragments = {
  referralReward: gql`
    fragment useReferralRewards_referralReward on ReferralReward {
      id
      shippingState
      conversionCredit {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
        percentageDiscount
      }
      card: anyCard {
        slug
        sport
        rarityTyped
        player: anyPlayer {
          slug
          age
          cardPositions
          activeClub {
            slug
            pictureUrl
          }
          anyPositions
        }
        ... on Card {
          slug
          backPictureUrl
        }
        ... on BaseballCard {
          slug
          anyTeam {
            slug
            pictureUrl
          }
        }
        ... on NBACard {
          slug
          anyTeam {
            slug
            pictureUrl
          }
        }
      }
      ...useClaimReferralReward_referralReward
    }
    ${monetaryAmountFragment}
    ${useClaimReferralReward.fragments.referralReward}
  ` as TypedDocumentNode<useReferralRewards_referralReward>,
};
